import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flame, Download, ChevronLeft, ChevronRight, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import PRShareCard from "./PRShareCard";

export default function PRModal({ prs, onClose }) {
    const [visible, setVisible] = useState(false);
    const [index, setIndex] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        if (prs && prs.length) {
            const t = setTimeout(() => setVisible(true), 50);
            return () => clearTimeout(t);
        }
    }, [prs]);

    const safePrs = useMemo(() => prs || [], [prs]);
    if (!safePrs.length) return null;
    const current = safePrs[index];

    const downloadCurrent = async () => {
        if (!cardRef.current) return;
        setDownloading(true);
        try {
            await new Promise(r => requestAnimationFrame(r));
            const dataUrl = await toPng(cardRef.current, {
                cacheBust: true, pixelRatio: 1, width: 1080, height: 1080, backgroundColor: "#0a0a0a",
            });
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `hypergains-pr-${current.exercise_id}-${Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("PR card saved.");
        } catch (e) {
            toast.error("Could not save the card.");
        } finally {
            setDownloading(false);
        }
    };

    const next = () => setIndex(i => (i + 1) % safePrs.length);
    const prev = () => setIndex(i => (i - 1 + safePrs.length) % safePrs.length);

    return (
        <>
            {/* Off-screen 1080x1080 share card for export */}
            <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", opacity: 0 }} aria-hidden="true">
                <PRShareCard ref={cardRef} pr={current} />
            </div>

            <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="pr-modal">
                <div className={`relative w-full max-w-lg transform transition-all duration-500 ${visible ? "scale-100 opacity-100" : "scale-90 opacity-0"}`}>
                    <button onClick={onClose} aria-label="Close" data-testid="pr-modal-close" className="absolute right-3 top-3 z-10 rounded-sm border border-white/20 bg-black/40 p-2 text-white hover:bg-black/60">
                        <X className="h-4 w-4" />
                    </button>

                    <div className="relative overflow-hidden border-2 border-accent-red bg-[#0a0a0a] p-8 text-white">
                        <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: "repeating-linear-gradient(135deg, rgba(255,59,48,0.12) 0 2px, transparent 2px 28px)" }} />

                        <div className="relative text-center">
                            <div className="flex items-center justify-center gap-2 label-eyebrow text-accent-red">
                                <Flame className="h-4 w-4" /> {safePrs.length > 1 ? `PRs Crushed · ${index + 1}/${safePrs.length}` : "PR Crushed"}
                            </div>
                            <h2 className="mt-3 font-display text-3xl font-black uppercase leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" data-testid="pr-modal-name">
                                {current.exercise_name}
                            </h2>
                            <p className="mt-1 text-xs text-white/55">
                                {current.first_time ? "First time logged · baseline locked in" : `Beat your previous est. 1RM by ${Math.round(current.delta_e1rm)} lb/kg`}
                            </p>

                            <div className="mt-5 grid grid-cols-3 gap-2">
                                <Stat label="Weight" value={Math.round(current.weight)} />
                                <Stat label="Reps" value={current.reps} />
                                <Stat label="Est. 1RM" value={Math.round(current.e1rm)} accent />
                            </div>

                            {current.exp_bonus != null && (
                                <p className="mt-4 text-sm text-white/85">
                                    Bonus: <span className="font-display text-base font-black text-accent-red">+{current.exp_bonus} EXP</span>
                                </p>
                            )}

                            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
                                <button onClick={downloadCurrent} disabled={downloading} data-testid="pr-modal-download" className="flex items-center justify-center gap-2 rounded-sm border border-accent-red bg-accent-red px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-60">
                                    {downloading ? <ImageIcon className="h-4 w-4 animate-pulse" /> : <Download className="h-4 w-4" />}
                                    {downloading ? "Saving" : "Save card"}
                                </button>
                                {safePrs.length > 1 && (
                                    <>
                                        <button onClick={prev} data-testid="pr-modal-prev" className="flex items-center justify-center gap-1 rounded-sm border border-white/30 bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/10">
                                            <ChevronLeft className="h-4 w-4" /> Prev
                                        </button>
                                        <button onClick={next} data-testid="pr-modal-next" className="flex items-center justify-center gap-1 rounded-sm border border-white/30 bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/10">
                                            Next <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </>
                                )}
                                {safePrs.length === 1 && (
                                    <button onClick={onClose} data-testid="pr-modal-continue" className="rounded-sm border border-white/30 bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/10 sm:col-span-2">
                                        Keep lifting
                                    </button>
                                )}
                            </div>
                            {safePrs.length > 1 && (
                                <button onClick={onClose} data-testid="pr-modal-done" className="mt-2 w-full rounded-sm border border-white/15 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white/70 hover:bg-white/5">
                                    Done · {safePrs.length} PRs unlocked
                                </button>
                            )}
                            <p className="mt-3 text-[11px] text-white/55">1080×1080 PNG · share each PR on your story</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`border px-3 py-2 ${accent ? "border-accent-red bg-accent-red/10" : "border-white/15 bg-white/5"}`}>
            <div className={`label-eyebrow ${accent ? "text-accent-red" : "text-white/55"}`}>{label}</div>
            <div className="mt-1 font-display text-2xl font-black tabular-nums">{value}</div>
        </div>
    );
}
